import React, { useEffect, useLayoutEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import CommonLayout from "../Common/Layout";

import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Button from "@mui/material/Button";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import MenuItem from "@mui/material/MenuItem";

import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDatePicker } from "@mui/x-date-pickers/MobileDatePicker";

import Dropzone from "../Common/Dropzone";
import { showToast } from "../../store/actions";
import JobService from "../../service/job";
import DeliveryFormatService from "../../service/deliveryformat";
import { useLocation, useNavigate } from "react-router-dom";
import { FilesContainer } from "../Common/Common";
import UserService from "../../service/user";

const useStyles = makeStyles((theme) =>
  createStyles({
    fileContainer: {
      padding: "8px 15px",
      background: "#1976d2",
      color: "#fff",
      borderTopLeftRadius: "5px",
      borderBottomLeftRadius: "5px",
    },
    fileNameContainer: {
      paddingLeft: "10px",
    },
    gridItem: {
      minHeight: "300px",
    },
    paper: {
      height: "100%",
      padding: "10px 20px",
    },
    imageContainer: {
      paddingTop: "20px",
    },
    detailsHeader: {
      fontWeight: "normal",
      "&::after": {
        content: "''",
        display: "block",
        width: "40px",
        height: "3px",
        background: "#EC5143",
        margin: "5px 0 0",
        borderRadius: "5px",
      },
    },
    StatusDrp: {
      "& .MuiSelect-select:focus": {
        backgroundColor: "transparent",
      },
    },
  })
);

const AddJob = () => {
  const { state } = useLocation();
  const user = useSelector((state) => state.user);
  const role = user.currentUser?.Role_id;
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    Category_id = null,
    CategoryName = null,
    SubCategory_id = null,
    SubCategoryName = null,
    JobId = null,
    CategoryProductLink = null,
  } = state;
  const [initialLoading, setInitialLoading] = useState(false);
  const [loading, setLoading] = useState(false);

  const [jobName, setJobName] = useState("");
  const [jobDescription, setJobDescription] = useState("");
  const [productLink, setProductLink] = useState("");
  const [imageFiles, setImageFiles] = useState([]);

  const [jobFiles, setJobFiles] = useState([]);
  const [removedFiles, setRemovedFiles] = useState([]);

  const [boxLayoutHeight, setBoxLayoutHeight] = useState(0);

  const [deliveryFormatsList, setDeliveryFormatsList] = useState([]);

  const [customersList, setCustomersList] = useState([]);
  const [customer, setCustomer] = useState("");

  const [supplierList, setSupplierList] = useState([]);
  const [supplier, setSupplier] = useState("");

  const [errors, setErrors] = useState({
    jobName: false,
    jobDescription: false,
    customer: false,
    supplier: false,
    purchaseOrder: false,
  });

  useEffect(() => {
    if (!JobId) {
      const DeliveryFormat = new DeliveryFormatService();
      DeliveryFormat.getDeliveryFormats({ category: Category_id, status: 1 }).then((response) => {
        if (response.success)
          setDeliveryFormatsList(
            response.data.map((data) => ({ id: data.delivery_format_id, name: data.text, value: "", type: data.type }))
          );
      });
    }

    // Load Customers list, only when Admin creates new job
    if (role === 1) {
      const userService = new UserService();

      userService.getCustomers().then((response) => {
        if (response.success && response.data?.userList?.length) {
          setCustomersList(response.data.userList);
        }
      });
    } else if (role === 2) {
      const userService = new UserService();

      userService.getSuppliers().then((response) => {
        if (response.success && response.data?.userList?.length) {
          setSupplierList(response.data.userList);
        }
      });
    }
  }, [JobId, Category_id]);

  useEffect(() => {
    if (JobId) {
      setInitialLoading(true);
      const Job = new JobService();

      Job.getJob(JobId).then((response) => {
        if (response.success) {
          const job = response.data;
          setJobFiles(job.Images.map(({ image }) => image));
          setJobName(job.Name || "");
          setJobDescription(job.Description || "");
          setProductLink(job.ProductLink || "");

          if (job.DeliveryFormats.length)
            setDeliveryFormatsList(
              job.DeliveryFormats.map((data) => ({ id: data.id, name: data.text, value: data.value, type: data.type }))
            );
        }

        setInitialLoading(false);
      });
    }
  }, [JobId]);

  useLayoutEffect(() => {
    function updateSize() {
      setBoxLayoutHeight(window.innerHeight - 180);
    }
    window.addEventListener("resize", updateSize);
    updateSize();

    return () => window.removeEventListener("resize", updateSize);
  }, []);

  const handleSubmit = async () => {
    setErrors((prevState) => {
      return {
        ...prevState,
        jobName: jobName.trim() === "",
        jobDescription: jobDescription.trim() === "",
        customer: role === 1 && customer === "",
        supplier: role === 2 && supplier === "",
        purchaseOrder: productLink.trim() === "",
      };
    });

    if (
      !(
        jobName.trim() === "" ||
        jobDescription.trim() === "" ||
        (role === 1 && customer === "") ||
        (role === 2 && supplier === "") ||
        productLink.trim() === ""
      )
    ) {
      setLoading(true);
      // Use FormData for uploading image
      const submitData = new FormData();

      submitData.append("Name", jobName.trim());
      submitData.append("Description", jobDescription.trim());
      submitData.append("Category_id", Category_id);
      submitData.append("CategoryName", CategoryName);
      submitData.append("SubCategory_id", SubCategory_id);
      submitData.append("SubCategoryName", SubCategoryName);
      imageFiles.forEach((img) => submitData.append("jobImg", img));
      submitData.append("DeliveryFormats", JSON.stringify(deliveryFormatsList));
      submitData.append("ProductLink", productLink);
      submitData.append("RemovedFiles", JSON.stringify(removedFiles));
      submitData.append("RemovedFilesType", 1);

      // Pass CustomerId in params, only when Admin creates new job
      if (role === 1) submitData.append("CustomerId", customer);
      // Pass SupplierId in params, only when Supplier creates new job
      else if (role === 2) submitData.append("SupplierId", supplier);

      const jobService = new JobService();

      let response = null;

      if (JobId) {
        // Update Job
        response = await jobService.updateJob(JobId, submitData);
      } else {
        // Add new Job
        response = await jobService.createJob(submitData);
      }

      setLoading(false);

      if (response.success) {
        dispatch(showToast({ message: "Job " + (JobId ? "updated" : "created") + " successfully.", type: "success" }));
        navigate(user.currentUser?.isAdminOrEmp === 1 ? "/admin/jobs" : "/jobs");
      } else {
        dispatch(showToast({ message: "Error in " + (JobId ? "updating" : "creating new") + " job.", type: "error" }));
      }
    }
  };

  const handleDeliveryFormatChange = (value, index) => {
    setDeliveryFormatsList((prev) =>
      prev.map((deliveryFormat, dfIndex) => {
        if (index == dfIndex) deliveryFormat.value = value;
        return deliveryFormat;
      })
    );
  };

  const handleRemoveFile = (file) => {
    const filesList = [...jobFiles];
    const fileIndex = filesList.findIndex((fl) => fl === file);
    const removedFile = filesList[fileIndex].split("/").pop();
    filesList.splice(fileIndex, 1);
    setJobFiles(filesList);
    setRemovedFiles((prev) => [...prev, removedFile]);
  };

  const breadcrumbArr = [{ link: "#", displayName: JobId ? "Edit Job" : "Add Job", active: true }];

  return (
    <CommonLayout title={JobId ? "Edit Job - " + JobId : "Add Job"} breadcrumbArr={breadcrumbArr}>
      <Container maxWidth="false" sx={{ mt: 4, mb: 4, mx: 0 }}>
        {initialLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "70vh",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Backdrop sx={{ color: "red", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
              <CircularProgress color="primary" />
            </Backdrop>
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  overflowY: "auto",
                  height: `${boxLayoutHeight}px`,
                  maxHeight: `${boxLayoutHeight}px`,
                }}
              >
                <Grid container spacing={2} justifyContent="center" height="100%">
                  <Grid item xs={4} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                      <h3 className={classes.detailsHeader}>
                        <b> Step 1: </b> Upload Your Images
                      </h3>
                      <Dropzone handleUploadFile={(files) => setImageFiles(files)} />

                      {JobId && <FilesContainer files={jobFiles} removeFile={handleRemoveFile} />}
                    </Paper>
                  </Grid>

                  <Grid item xs={4} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                      <h3 className={classes.detailsHeader}>
                        <b> Step 2: </b>
                        Job Name & Instructions
                        <span style={{ color: "#dc3545" }}> *</span>
                      </h3>

                      <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                        Job Name
                      </InputLabel>
                      <TextField
                        margin="normal"
                        value={jobName}
                        onChange={(e) => setJobName(e.target.value)}
                        fullWidth
                        variant="filled"
                        placeholder="Enter Your Job Name"
                        hiddenLabel
                        sx={{ mt: 1 }}
                        size="small"
                        error={errors.jobName}
                        helperText={errors.jobName ? "Please enter Job Name." : ""}
                      />
                      {role === 2 && (
                        <>
                          <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                            Select Supplier
                          </InputLabel>

                          <TextField
                            margin="normal"
                            fullWidth
                            variant="filled"
                            value={supplier}
                            onChange={(e) => setSupplier(e.target.value)}
                            select
                            sx={{ mt: 1 }}
                            hiddenLabel
                            size="small"
                            className={classes.StatusDrp}
                            error={errors.supplier}
                            helperText={errors.supplier ? "Please select Supplier!" : ""}
                          >
                            {supplierList.map((supplier) => (
                              <MenuItem key={supplier.User_id} value={supplier.User_id}>
                                {supplier.FirstName} {supplier.LastName}
                              </MenuItem>
                            ))}
                          </TextField>
                        </>
                      )}

                      {role === 1 && (
                        <TextField
                          margin="normal"
                          select
                          label="Select Customer"
                          variant="filled"
                          size="small"
                          fullWidth
                          value={customer}
                          onChange={(e) => setCustomer(e.target.value)}
                          error={errors.customer}
                          helperText={errors.customer ? "Please select Customer!" : ""}
                        >
                          {customersList.map((customer) => (
                            <MenuItem key={customer.User_id} value={customer.User_id}>
                              {customer.FirstName} {customer.LastName}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}

                      <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper", mt: 1 }}>
                        <ListItem alignItems="flex-start" sx={{ p: 0 }}>
                          <ListItemText primary="Category" sx={{ m: 0 }} secondary={CategoryName} />
                        </ListItem>
                      </List>

                      {SubCategoryName && (
                        <List sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper", mt: 1 }}>
                          <ListItem alignItems="flex-start" sx={{ p: 0 }}>
                            <ListItemText primary="Sub Category" sx={{ m: 0 }} secondary={SubCategoryName} />
                          </ListItem>
                        </List>
                      )}

                      <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                        Job Description
                      </InputLabel>
                      <TextField
                        margin="normal"
                        value={jobDescription}
                        onChange={(e) => setJobDescription(e.target.value)}
                        fullWidth
                        variant="filled"
                        placeholder="Enter Job Description Here"
                        hiddenLabel
                        sx={{ mt: 1 }}
                        multiline
                        rows={5}
                        error={errors.jobDescription}
                        helperText={errors.jobDescription ? "Please enter Job Description." : ""}
                      />

                      {CategoryProductLink == 1 && (
                        <>
                          <InputLabel sx={{ mt: 1 }} className="inputFormCustomLabel">
                            Purchase Order
                          </InputLabel>
                          <TextField
                            margin="normal"
                            value={productLink}
                            onChange={(e) => setProductLink(e.target.value)}
                            fullWidth
                            variant="filled"
                            placeholder="Enter Purchase Order for job"
                            size="small"
                            hiddenLabel
                            sx={{ mt: 1 }}
                            error={errors.purchaseOrder}
                            helperText={errors.purchaseOrder ? "Please enter Purchase Order." : ""}
                          />
                        </>
                      )}
                    </Paper>
                  </Grid>

                  <Grid item xs={4} className={classes.gridItem}>
                    <Paper className={classes.paper}>
                      <h3 className={classes.detailsHeader}>
                        <b> Step 3: </b>
                        Delivery Format
                      </h3>

                      {deliveryFormatsList.map((dlf, dlfIndex) => (
                        <Box key={dlf.id} sx={{ mt: 1 }}>
                          <InputLabel style={{ fontSize: "14px" }} sx={{ mt: 1 }} className="inputFormCustomLabel">
                            {dlf.name}
                          </InputLabel>
                          {dlf.type === "text" ? (
                            <TextField
                              margin="normal"
                              value={dlf.value}
                              onChange={(e) => handleDeliveryFormatChange(e.target.value, dlfIndex)}
                              required
                              fullWidth
                              variant="filled"
                              size="small"
                              hiddenLabel
                              sx={{ mt: 1 }}
                            />
                          ) : (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <MobileDatePicker
                                value={dlf.value}
                                disablePast
                                onChange={(date) => {
                                  handleDeliveryFormatChange(
                                    date ? new Date(date).toISOString().slice(0, 19).replace("T", " ") : "",
                                    dlfIndex
                                  );
                                }}
                                renderInput={(params) => (
                                  <TextField
                                    margin="normal"
                                    sx={{ mt: 1, cursor: "pointer", input: { cursor: "pointer" } }}
                                    fullWidth
                                    variant="filled"
                                    size="small"
                                    hiddenLabel
                                    {...params}
                                    InputProps={{
                                      readOnly: true,
                                    }}
                                  />
                                )}
                              />
                            </LocalizationProvider>
                          )}
                        </Box>
                      ))}
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
              <Box sx={{ flex: "1 1 auto" }} />
              {JobId && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => navigate("/jobs")}
                  style={{
                    backgroundColor: "#3a3d4a",
                    padding: "5px 20px",
                  }}
                >
                  CANCEL
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                onClick={handleSubmit}
                style={{
                  backgroundColor: "#1b5e20",
                  padding: "5px 20px",
                  marginLeft: "15px",
                }}
              >
                {JobId ? "UPDATE" : "FIND & QUOTE"}
              </Button>
            </Box>
          </>
        )}
      </Container>
    </CommonLayout>
  );
};

export default AddJob;
